import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { fontScale } from '@/theme';
import { fluidSpace } from '@/theme/scalingTokens';
import type { EventState } from '@/types/events.types';

/**
 * Figma-matched status tag colors
 * Based on: https://www.figma.com/design/vPvAacd1QiZGAj2OLTKzmT/GWS---Event-Management-tool?node-id=206-9589
 */
export const useStatusTagStyles = makeStyles({
  badge: {
    display: 'inline-block',
    ...shorthands.padding(fluidSpace.xxs, fluidSpace.sm),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    fontSize: fontScale.caption,
    lineHeight: '1.35',
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: 'nowrap',
    textAlign: 'center',
    verticalAlign: 'middle',
    '@media (max-width: 1750px)': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  // Draft: Comm Blue Tint 30
  draft: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    color: tokens.colorNeutralForeground1,
  },
  // Submitted: New request
  newRequest: {
    backgroundColor: `var(--gem-status-new-request-background, ${tokens.colorPaletteYellowBackground2})`,
    color: `var(--gem-status-new-request-foreground, ${tokens.colorPaletteRedForeground2})`,
  },
  // Under Review
  awaitingApproval: {
    backgroundColor: `var(--gem-status-under-review-background, ${tokens.colorPaletteMarigoldBackground2})`,
    color: `var(--gem-status-under-review-foreground, ${tokens.colorPaletteRedForeground2})`,
  },
  // Pending Cancellation: Dark orange background to distinguish from New Request
  warning: {
    backgroundColor: tokens.colorPaletteDarkOrangeBackground2,
    color: tokens.colorPaletteDarkOrangeForeground2,
  },
  // Confirmed: Light green
  confirmed: {
    backgroundColor: tokens.colorPaletteLightGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  // Declined/No Occurrence: Light rose
  danger: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
  },
  // Cancelled: Soft cranberry red — user-induced terminal state, distinct from
  // the Declined red (admin rejection) and less severe in tone.
  cancelled: {
    backgroundColor: tokens.colorPaletteCranberryBackground2,
    color: tokens.colorPaletteCranberryForeground2,
  },
  // Completed/No Occurrence: Neutral gray
  terminalNeutral: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
});

/**
 * Map EventState to style classes matching Figma design
 */
export const getStatusStyle = (
  state: EventState,
  styles: ReturnType<typeof useStatusTagStyles>
): string => {
  switch (state) {
    case 'DRAFT':
      return styles.draft;
    case 'SUBMITTED':
      return styles.newRequest;
    case 'UNDER_REVIEW':
      return styles.awaitingApproval;
    case 'SUBMITTED_FOR_CANCELLATION':
      return styles.warning;
    case 'CONFIRMED':
      return styles.confirmed;
    case 'DECLINED':
      return styles.danger;
    case 'CANCELLED':
      return styles.cancelled;
    case 'COMPLETED':
    case 'NO_OCCURRENCE':
      return styles.terminalNeutral;
    default:
      return styles.draft;
  }
};
